import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faRocket, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FaFacebook } from 'react-icons/fa';
import TwilioPlatform from './TwilioPlatform';
import VapiPlatform from './VapiPlatform';
import OpenAIPlatform from './OpenAIPlatform';
import MetaPlatform from './MetaPlatform';

// Animações e estilos
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
  50% { box-shadow: 0 0 15px rgba(64, 128, 255, 0.3); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
`;

// Componentes estilizados
const GuideContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 0.8s ease-out;
`;

const PlatformNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2.5rem;
  position: relative;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(64, 128, 255, 0.2);
  animation: ${glowPulse} 4s infinite;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 10%;
    right: 10%;
    height: 2px;
    background: rgba(64, 128, 255, 0.2);
    z-index: 0;
  }
`;

const PlatformButton = styled.button`
  background: transparent;
  border: none;
  padding: 1rem 1.5rem;
  color: ${props => props.active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  flex: 1;
  
  &:hover {
    color: #fff;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 25%;
    right: 25%;
    height: 3px;
    background: ${props => props.active ? props.color : 'transparent'};
    transform: ${props => props.active ? 'scaleX(1)' : 'scaleX(0)'};
    transform-origin: center;
    transition: transform 0.3s ease;
    z-index: 1;
    border-radius: 3px;
  }
  
  &:hover::after {
    transform: scaleX(1);
    background: ${props => props.active ? props.color : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const PlatformIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.completed ? props.color : 'rgba(0, 0, 0, 0.3)'};
  margin-bottom: 0.8rem;
  color: ${props => props.completed ? '#000' : props.color};
  border: 2px solid ${props => props.color};
  font-size: 0.9rem;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: ${props => props.active ? `0 0 10px ${props.color}` : 'none'};
  
  svg {
    filter: ${props => props.completed ? 'none' : `drop-shadow(0 0 2px ${props.color})`};
  }
  
  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
  
  &::before {
    content: '${props => props.number}';
    position: absolute;
    top: -8px;
    right: -8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.color};
    color: #000;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  }
`;

const PlatformLabel = styled.div`
  font-size: 1rem;
  color: ${props => props.active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  text-align: center;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.3s ease;
`;

const ProgressBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(to right, ${props => props.color}, ${props => props.color}80);
  transition: width 0.5s ease;
`;

const NextButton = styled.button`
  background: linear-gradient(135deg, #4C6EF5, #6E8FFF);
  border: none;
  border-radius: 4px;
  padding: 0.8rem 1.5rem;
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(76, 110, 245, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(76, 110, 245, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    margin-left: 0.5rem;
  }
  
  &:disabled {
    background: rgba(76, 110, 245, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const NavigationButton = styled.button`
  background: linear-gradient(135deg, #4C6EF5, #3B55C5);
  padding: 12px 24px;
  font-size: 1.1rem;
  border: none;
  border-radius: 4px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(76, 110, 245, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(76, 110, 245, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    margin-left: 0.5rem;
  }
  
  &:disabled {
    background: rgba(76, 110, 245, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FinalSuccessMessage = styled.div`
  text-align: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  color: #fff;
`;

const PlatformGuide = ({ onComplete }) => {
  const [activePlatform, setActivePlatform] = useState('twilio');
  const [completedSteps, setCompletedSteps] = useState({
    twilio: Array(9).fill(false),
    vapi: Array(6).fill(false),
    openai: Array(7).fill(false),
    meta: [false]
  });
  
  // Adicionar estado para armazenar o passo ativo de cada plataforma
  const [activeSteps, setActiveSteps] = useState({
    twilio: 0,
    vapi: 0,
    openai: 0,
    whatsapp: 0
  });
  
  const [platformProgress, setPlatformProgress] = useState({
    twilio: 0,
    vapi: 0,
    openai: 0,
    whatsapp: 0
  });
  
  const [showError, setShowError] = useState(false);
  const [credentials, setCredentials] = useState({
    vapiApiKey: '',
    twilioAccountSid: '',
    twilioAuthToken: '',
    twilioCallerId: '',
    openaiApiKey: '',
    
  });
  
  const platforms = [
    { id: 'twilio', name: 'Twilio', color: '#F22F46', logo: '/twilio_logo.png' },
    { id: 'meta', name: 'Meta', color: '#1877F3', logo: '/meta_logo.png' },
    { id: 'vapi', name: 'Vapi', color: '#00A67E', logo: '/vapi_logo.png' },
    { id: 'openai', name: 'OpenAI', color: '#9B59B6', logo: '/openai_logo.png' }
  ];
  
  useEffect(() => {
    // Calcular progresso para cada plataforma
    const newProgress = {};
    
    Object.keys(completedSteps).forEach(platform => {
      const completed = completedSteps[platform].filter(step => step).length;
      const total = completedSteps[platform].length;
      newProgress[platform] = Math.round((completed / total) * 100);
    });
    
    setPlatformProgress(newProgress);
  }, [completedSteps]);
  
  const handleStepComplete = (platformId, stepIndex) => {
    setCompletedSteps(prev => ({
      ...prev,
      [platformId]: prev[platformId].map((step, idx) => 
        idx === stepIndex ? !step : step
      )
    }));
  };
  
  // Função para atualizar o passo ativo de uma plataforma
  const updateActiveStep = (platformId, stepIndex) => {
    setActiveSteps(prev => ({
      ...prev,
      [platformId]: stepIndex
    }));
  };
  
  const handleNextPlatform = () => {
    const currentIndex = platforms.findIndex(p => p.id === activePlatform);
    if (currentIndex < platforms.length - 1) {
      setActivePlatform(platforms[currentIndex + 1].id);
    }
  };
  
  const isPlatformComplete = (platformId) => {
    return completedSteps[platformId].every(step => step);
  };
  
  const isCurrentPlatformComplete = isPlatformComplete(activePlatform);
  const isLastPlatform = activePlatform === 'meta';
  const isAllComplete = isPlatformComplete('twilio') && isPlatformComplete('vapi') && isPlatformComplete('openai') && isPlatformComplete('meta');
  
  // Função para atualizar credenciais
  const updateCredential = (platform, name, value) => {
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    console.log(`Credencial atualizada: ${platform} - ${name}: ${value}`);
  };
  
  // Função para enviar credenciais quando todas plataformas estiverem completas
  const submitAllCredentials = async () => {
    try {
      // Obter telefone do usuário do localStorage
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userPhone = userData.telefone || '';
      
      setShowError(false);
      
      // Enviar para o webhook existente
      const response = await fetch('https://webhook-editor.abianca.com.br/webhook/41ae71d3-dc9a-47d1-8d22-c0334ba645e6', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          telefone: userPhone,
          ...credentials
        })
      });
      
      const responseText = await response.text();
      
      // Verificar resposta
      if (responseText.toLowerCase().includes('sucesso')) {
        // Mostrar mensagem de parabéns
        if (onComplete) {
          onComplete();
        }
        return true;
      } else {
        // Mostrar erro
        setShowError(true);
        console.error('Erro na resposta do webhook:', responseText);
        throw new Error('Resposta do webhook não contém "sucesso"');
      }
    } catch (error) {
      console.error('Erro ao enviar credenciais:', error);
      setShowError(true);
      throw error; // Re-lançar o erro para ser tratado pelo componente OpenAIPlatform
    }
  };
  
  return (
    <GuideContainer>
      <PlatformNavigation>
        {platforms.map((platform, index) => {
          const isActive = activePlatform === platform.id;
          const isCompleted = isPlatformComplete(platform.id);
          
          return (
            <PlatformButton 
              key={platform.id}
              active={isActive}
              color={platform.color}
              onClick={() => setActivePlatform(platform.id)}
            >
              <PlatformIcon 
                active={isActive} 
                completed={isCompleted}
                color={platform.color}
                number={index + 1}
              >
                {platform.icon ? platform.icon : (
                  <>
                    {platform.id === 'meta' ? (
                      <img src={platform.logo} alt={`${platform.name} Logo`} style={{ filter: 'brightness(0) invert(1)' }} />
                    ) : (
                      <img src={platform.logo} alt={`${platform.name} Logo`} />
                    )}
                  </>
                )}
              </PlatformIcon>
              <PlatformLabel active={isActive}>{platform.name}</PlatformLabel>
              {isActive && (
                <ProgressBar>
                  <ProgressFill 
                    progress={platformProgress[platform.id]} 
                    color={platform.color}
                  />
                </ProgressBar>
              )}
            </PlatformButton>
          );
        })}
      </PlatformNavigation>
      
      {activePlatform === 'twilio' && (
        <TwilioPlatform 
          completedSteps={completedSteps.twilio}
          onStepComplete={(index) => handleStepComplete('twilio', index)}
          onNavigateToNextPlatform={() => handleNextPlatform()}
          onUpdateCredential={updateCredential}
          platformColor={platforms.find(p => p.id === activePlatform)?.color || '#F22F46'}
          activeStep={activeSteps.twilio}
          onUpdateActiveStep={(stepIndex) => updateActiveStep('twilio', stepIndex)}
        />
      )}
      
      {activePlatform === 'vapi' && (
        <VapiPlatform 
          completedSteps={completedSteps.vapi}
          onStepComplete={(index) => handleStepComplete('vapi', index)}
          onNavigateToNextPlatform={() => handleNextPlatform()}
          onUpdateCredential={updateCredential}
          platformColor={platforms.find(p => p.id === activePlatform)?.color || '#00A67E'}
          activeStep={activeSteps.vapi}
          onUpdateActiveStep={(stepIndex) => updateActiveStep('vapi', stepIndex)}
        />
      )}
      
      {activePlatform === 'meta' && (
        <MetaPlatform
          onStepComplete={index => handleStepComplete('meta', index)}
          onNavigateToNextPlatform={handleNextPlatform}
        />
      )}
      
      {activePlatform === 'openai' && (
        <OpenAIPlatform 
          completedSteps={completedSteps.openai}
          onStepComplete={(index) => handleStepComplete('openai', index)}
          allPlatformsComplete={isAllComplete}
          onUpdateCredential={updateCredential}
          onSubmitCredentials={submitAllCredentials}
          platformColor={platforms.find(p => p.id === activePlatform)?.color || '#4C6EF5'}
          activeStep={activeSteps.openai}
          onUpdateActiveStep={(stepIndex) => updateActiveStep('openai', stepIndex)}
        />
      )}
    </GuideContainer>
  );
};

export default PlatformGuide;
